/**
 * Enhanced Confirmation Dialog
 * Shows detailed action preview with trust controls
 */
import React, { useState } from 'react';
import { Modal, Button, Space, Tag, Alert, Checkbox, Collapse } from 'antd';
import styled from 'styled-components';
import { 
  CheckCircleOutlined, 
  WarningOutlined, 
  InfoCircleOutlined,
  DatabaseOutlined,
  ApiOutlined 
} from '@ant-design/icons';

const { Panel } = Collapse;

const ActionPreview = styled.div`
  margin: 16px 0;
  padding: 16px;
  background: var(--color-gray-50);
  border-radius: 8px;
  border: 1px solid var(--color-border);
`;

const ActionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const ActionIcon = styled.div<{ $type: string }>`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  
  ${props => {
    switch (props.$type) {
      case 'create':
        return `
          background: var(--color-success-light);
          color: var(--color-success);
        `;
      case 'modify':
        return `
          background: var(--color-warning-light);
          color: var(--color-warning);
        `;
      case 'external':
        return `
          background: var(--color-info-light);
          color: var(--color-info);
        `;
      default:
        return `
          background: var(--color-gray-200);
          color: var(--color-gray-600);
        `;
    }
  }}
`;

const ActionDetails = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: white;
  border-radius: 4px;
  font-size: 13px;
`;

const ImpactList = styled.ul`
  margin: 8px 0;
  padding-left: 20px;
  
  li {
    margin: 4px 0;
  }
`;

const TrustSection = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: var(--color-info-light);
  border-radius: 8px;
`;

interface EnhancedConfirmationDialogProps {
  open: boolean;
  actionPlan: any;
  onConfirm: (trustSettings?: any) => void;
  onCancel: () => void;
  userTrustLevel?: 'low' | 'medium' | 'high';
}

export const EnhancedConfirmationDialog: React.FC<EnhancedConfirmationDialogProps> = ({
  open,
  actionPlan,
  onConfirm,
  onCancel,
  userTrustLevel = 'low',
}) => {
  const [skipFutureConfirmations, setSkipFutureConfirmations] = useState(false);
  const [trustForSession, setTrustForSession] = useState(false);
  
  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'create_dashboard':
      case 'track_metric':
        return <DatabaseOutlined />;
      case 'navigate':
        return <InfoCircleOutlined />;
      case 'export_data':
        return <ApiOutlined />;
      default:
        return <WarningOutlined />;
    }
  };
  
  const getActionType = (actionType: string) => {
    if (actionType.includes('create')) return 'create';
    if (actionType.includes('modify')) return 'modify';
    if (actionType.includes('external')) return 'external';
    return 'other';
  };
  
  const getImpactLevel = (action: any) => {
    // Determine impact level
    if (action.type.includes('delete')) return 'high';
    if (action.type.includes('create')) return 'medium';
    if (action.type.includes('navigate')) return 'low';
    return 'medium';
  };
  
  const renderActionPreview = (action: any, index: number) => {
    const impact = getImpactLevel(action);
    const actionTypeCategory = getActionType(action.type);
    
    return (
      <ActionPreview key={index}>
        <ActionHeader>
          <ActionIcon $type={actionTypeCategory}>
            {getActionIcon(action.type)}
          </ActionIcon>
          <div style={{ flex: 1 }}>
            <strong>{action.description}</strong>
            <div>
              <Tag>{action.type.replace(/_/g, ' ').toUpperCase()}</Tag>
              <Tag color={
                impact === 'high' ? 'error' : 
                impact === 'medium' ? 'warning' : 
                'success'
              }>
                {impact.toUpperCase()} IMPACT
              </Tag>
            </div>
          </div>
        </ActionHeader>
        
        <Collapse ghost>
          <Panel header="View Details" key="1">
            <ActionDetails>
              {action.type === 'create_dashboard' && (
                <>
                  <strong>Dashboard Details:</strong>
                  <ImpactList>
                    <li>Title: {action.params.title}</li>
                    <li>Widgets: {action.params.widgets?.length || 0}</li>
                    <li>Type: {action.params.layout_type}</li>
                  </ImpactList>
                </>
              )}
              
              {action.type === 'navigate' && (
                <>
                  <strong>Navigation:</strong>
                  <ImpactList>
                    <li>Target: {action.params.page}</li>
                    <li>URL: {action.params.url}</li>
                  </ImpactList>
                </>
              )}
              
              {action.type === 'export_data' && (
                <>
                  <strong>Export Details:</strong>
                  <ImpactList>
                    <li>Type: {action.params.export_type}</li>
                    <li>Format: {action.params.config?.format}</li>
                    <li>Filename: {action.params.filename}</li>
                  </ImpactList>
                </>
              )}
              
              <div style={{ marginTop: 12 }}>
                <strong>What will happen:</strong>
                <ImpactList>
                  {action.type === 'create_dashboard' && (
                    <>
                      <li>A new dashboard will be created</li>
                      <li>It will appear in your dashboards list</li>
                      <li>You can modify or delete it later</li>
                    </>
                  )}
                  {action.type === 'navigate' && (
                    <>
                      <li>You will be taken to a different page</li>
                      <li>Your current work will be saved</li>
                    </>
                  )}
                  {action.type === 'export_data' && (
                    <>
                      <li>Data will be downloaded to your device</li>
                      <li>The file will contain church data</li>
                      <li>Ensure secure storage of exported data</li>
                    </>
                  )}
                </ImpactList>
              </div>
            </ActionDetails>
          </Panel>
        </Collapse>
      </ActionPreview>
    );
  };
  
  const hasHighImpactActions = actionPlan?.action_plan?.actions?.some(
    (action: any) => getImpactLevel(action) === 'high'
  );
  
  return (
    <Modal
      title="Confirm Actions"
      open={open}
      onCancel={onCancel}
      width={600}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button 
          key="confirm" 
          type="primary" 
          onClick={() => onConfirm({
            skipFutureConfirmations,
            trustForSession,
          })}
        >
          Confirm & Execute
        </Button>,
      ]}
    >
      {hasHighImpactActions && (
        <Alert
          message="High Impact Actions"
          description="Some of these actions will make significant changes."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      
      <p>
        {actionPlan?.confirmation_message || 
         'I will perform the following actions:'}
      </p>
      
      {actionPlan?.action_plan?.actions?.map(renderActionPreview)}
      
      {userTrustLevel !== 'low' && (
        <TrustSection>
          <strong>Trust Settings:</strong>
          <div style={{ marginTop: 8 }}>
            <Checkbox
              checked={skipFutureConfirmations}
              onChange={(e) => setSkipFutureConfirmations(e.target.checked)}
              disabled={hasHighImpactActions}
            >
              Don't ask for confirmation for similar actions
            </Checkbox>
          </div>
          <div style={{ marginTop: 4 }}>
            <Checkbox
              checked={trustForSession}
              onChange={(e) => setTrustForSession(e.target.checked)}
            >
              Trust all actions for this session
            </Checkbox>
          </div>
          {hasHighImpactActions && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
              High impact actions always require confirmation
            </div>
          )}
        </TrustSection>
      )}
      
      <div style={{ marginTop: 16, fontSize: 12, color: 'var(--text-secondary)' }}>
        <InfoCircleOutlined /> All actions can be undone within 24 hours
      </div>
    </Modal>
  );
};